import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import ContactConciergeModal from "@/components/ContactConciergeModal";
import { useDayPass } from "@/contexts/DayPassContext";

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

const FAQItem: React.FC<FAQItemProps> = ({
  question,
  answer,
  isOpen,
  onToggle,
}) => {
  return (
    <div
      className="border-b border-swiss-cream/20 last:border-0"
      data-oid="faq-item"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-6 text-left hover:bg-pebble-beige/20 transition-colors px-4 sm:px-6"
        aria-expanded={isOpen}
        data-oid="faq-toggle"
      >
        <h3
          className="text-lg sm:text-xl font-serif text-gray-900 pr-4"
          data-oid="faq-question"
        >
          {question}
        </h3>
        <span className="flex-shrink-0 text-alpine-gold" data-oid="faq-chevron">
          {isOpen ? (
            <ChevronUp size={24} data-oid="chevron-up" />
          ) : (
            <ChevronDown size={24} data-oid="chevron-down" />
          )}
        </span>
      </button>

      {isOpen && (
        <div className="px-4 sm:px-6 pb-6 animate-fadeIn" data-oid="faq-answer">
          <p
            className="text-base sm:text-lg text-gray-700 leading-relaxed"
            data-oid="faq-answer-text"
          >
            {answer}
          </p>
        </div>
      )}
    </div>
  );
};

const DayGuestFAQ: React.FC = () => {
  const { t, language } = useLanguage();
  const { setIsTrayOpen } = useDayPass();
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [isContactOpen, setIsContactOpen] = useState(false);

  // Get FAQ questions and answers from translations
  const faqs = [
    {
      question: t("activities.faq.questions.whatIncluded.question"),
      answer: t("activities.faq.questions.whatIncluded.answer"),
    },
    {
      question: t("activities.faq.questions.advanceBooking.question"),
      answer: t("activities.faq.questions.advanceBooking.answer"),
    },
    {
      question: t("activities.faq.questions.upgradeMassage.question"),
      answer: t("activities.faq.questions.upgradeMassage.answer"),
    },
    {
      question: t("activities.faq.questions.guestLimit.question"),
      answer: t("activities.faq.questions.guestLimit.answer"),
    },
    {
      question: t("activities.faq.questions.multipleActivities.question"),
      answer: t("activities.faq.questions.multipleActivities.answer"),
    },
    {
      question: t("activities.faq.questions.parking.question"),
      answer: t("activities.faq.questions.parking.answer"),
    },
    {
      question: t("activities.faq.questions.mealSelection.question"),
      answer: t("activities.faq.questions.mealSelection.answer"),
    },
    {
      question: t("activities.faq.questions.whatToBring.question"),
      answer: t("activities.faq.questions.whatToBring.answer"),
    },
    {
      question: t("activities.faq.questions.familyFriendly.question"),
      answer: t("activities.faq.questions.familyFriendly.answer"),
    },
    {
      question: t("activities.faq.questions.operatingHours.question"),
      answer: t("activities.faq.questions.operatingHours.answer"),
    },
    {
      question: t("activities.faq.questions.cancellationPolicy.question"),
      answer: t("activities.faq.questions.cancellationPolicy.answer"),
    },
  ];

  return (
    <section
      className="py-20 md:py-24 bg-pebble-beige"
      data-oid="day-guest-faq-section"
    >
      <div
        className="container mx-auto px-4 sm:px-6 max-w-4xl"
        data-oid="faq-container"
      >
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16" data-oid="faq-header">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-serif text-gray-900 mb-4"
            data-oid="faq-title"
          >
            {t("activities.faq.title")}
          </h2>
          <p className="text-lg sm:text-xl text-gray-700" data-oid="faq-subtitle">
            {t("activities.faq.subtitle")}
          </p>
        </div>

        {/* FAQ List */}
        <div
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-swiss-cream/20"
          data-oid="faq-list"
        >
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
              data-oid={`faq-item-${index}`}
            />
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center" data-oid="faq-cta">
          <p
            className="text-base sm:text-lg text-gray-700 mb-6"
            data-oid="faq-cta-text"
          >
            {t("activities.faq.ctaText")}
          </p>
          <Button
            onClick={() => setIsContactOpen(true)}
            trackingType="contact_modal"
            trackingLabel="Contact Concierge"
            trackingLocation="activities_faq_section"
            trackingData={{ modalType: "concierge" }}
            variant="outline"
            className="px-6 py-3 border-2 border-alpine-gold text-alpine-gold hover:bg-alpine-gold hover:text-white rounded-full font-medium transition-all duration-300"
            data-oid="faq-contact-button"
          >
            {t("activities.faq.ctaButton")}
          </Button>
        </div>
      </div>

      {/* Contact Modal (Activities context uses activity_* contact events) */}
      <ContactConciergeModal
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
        trackingContext="activity"
        onBookDayPass={() => setIsTrayOpen(true, "activities_faq_modal")}
      />
    </section>
  );
};

export default DayGuestFAQ;
